import {DWS_RECORDS} from '../data/noberoFreeze'
import {RULES} from '../data/reference'

/**
 * DWS evidence rollup — the public contract of the DWS images section.
 * Initiate's submit gate and its attach picker both key off this file. This
 * is the ONE evidence rollup for the module; a competing version was
 * considered for Initiate and rejected on the merits (it rounded
 * percentages, keyed on `row.key` only so it broke for SKUs, had no
 * session-upload channel, and let `merchantPct` exceed 100). Initiate
 * imports this file rather than defining its own.
 *
 * TWO SOURCES, NEVER SUMMED:
 *   sorter    the courier's DWS hub scan. Arrives on its own; nothing the
 *             merchant does moves it.
 *   merchant  the packing video and stills the merchant supplies. Only the
 *             merchant can produce these, and the Initiate gate is on THIS
 *             count being zero — never on `!both`, because a SKU the courier
 *             happens not to have scanned is not the merchant's fault.
 *
 * A SKU with ten sorter records and no packing video is not 10/10 evidenced.
 * It is missing the half the merchant owns. So there is deliberately no
 * combined `evidence` or `total` field on the returned object, and a test
 * asserts their absence.
 *
 * ONE FIX AGAINST THE MOCKUP: the mockup seeded a merchant baseline from a
 * hash of the SKU code, with no records behind it, so the table's count and
 * the gallery's thumbnails disagreed and the gallery caption had to
 * deliberately avoid the rollup. Here `merchantCount` equals the number of
 * `source: 'upload'` records on file, by construction in the generator
 * (asserted in data/noberoFreeze.test.js), so one number serves both:
 * `merchant = row.merchantCount + <session uploads for this row>`.
 *
 * SESSION UPLOADS ARE NOT PERSISTED. `uploads` is React state held by the
 * page and threaded through every function here as a first-class parameter.
 * A page reload loses it. There is no API; the dataset is frozen and local.
 *
 * There are no real DWS photographs anywhere in this dataset. `source:
 * 'courier'` records carry `images: []` by construction, and no function
 * here ever fabricates an image path or url for one.
 */

/** Records are keyed by SKU code or by combo key — one index serves both. */
const BY_KEY = DWS_RECORDS.reduce((map, rec) => {
  const list = map[rec.sku] || (map[rec.sku] = [])
  list.push(rec)
  return map
}, {})

/** A row is a SKU (keyed by `sku`) or a combo (keyed by `key`) — never both. */
export function rowKey(row) {
  return (row && (row.sku || row.key)) || ''
}

function uploadsFor(row, uploads) {
  const id = rowKey(row)
  return (uploads || []).filter((u) => u.sku === id)
}

/**
 * Everything on file for one row: this session's uploads first, then the
 * dataset's records, trimmed to the shared retention window. The cap is
 * shared between the two sources — that is what DWS_RETAIN promises. A row
 * with nothing on file (real for some combos) returns an empty array.
 */
export function dwsRecordsFor(row, uploads) {
  return uploadsFor(row, uploads)
    .concat(BY_KEY[rowKey(row)] || [])
    .slice(0, RULES.DWS_RETENTION_RECORDS)
}

export function dwsEvidence(row, uploads) {
  const ships = (row && row.ships) || 0
  const sorter = (row && row.sorterCount) || 0
  const ups = uploadsFor(row, uploads)
  const merchant = ((row && row.merchantCount) || 0) + ups.length

  const onFile = dwsRecordsFor(row, uploads)
  const courierOnFile = onFile.filter((r) => r.source === 'courier').length

  // Unrounded on purpose. Rounding here turns a real 0.4% into 0% and
  // defeats pct()'s 'Under 1%' rule, which exists because a sub-1% figure is
  // a genuine measurement, not "no data". Only the renderer rounds.
  const sorterPct = ships ? (sorter / ships) * 100 : 0
  const merchantPct = ships ? (Math.min(merchant, ships) / ships) * 100 : 0

  return {
    sorter,
    merchant,
    uploaded: ups.length,
    // Records on file carrying a packing video, seeded and session alike.
    video: onFile.filter((r) => r.video).length,
    sorterPct,
    merchantPct,
    // Sorter readings with no record retained — the 10-record window is
    // already applied in the frozen dataset, so this is computed from what
    // is actually on file rather than assumed.
    beyondRetention: Math.max(0, sorter - courierOnFile),
    // The three states the freeze form gates on.
    none: sorter === 0 && merchant === 0,
    partial: (sorter === 0) !== (merchant === 0),
    both: sorter > 0 && merchant > 0,
  }
}

/**
 * Computes every row's evidence exactly once for a given (rows, uploads)
 * pair, keyed by rowKey. `dwsEvidence` otherwise runs inside a filter
 * predicate, a sort accessor AND a cell renderer for the same row on the
 * same keystroke — an O(rows) rebuild of DWS_RECORDS lookups times 3 per
 * render. Callers (the page, `filterSkus`, `coverageCounts`, `dwsStats`)
 * build this once and read from it instead of calling `dwsEvidence`
 * repeatedly for the same row.
 */
export function buildEvidenceIndex(rows, uploads) {
  const index = new Map()
  ;(rows || []).forEach((row) => {
    index.set(rowKey(row), dwsEvidence(row, uploads))
  })
  return index
}

/**
 * Coverage filters, addressed by STRING ID. Never by array index — a reorder
 * of this list must not silently change what a saved filter means.
 *
 * Each `match` accepts an optional pre-computed evidence object as its third
 * argument (from `buildEvidenceIndex`) so callers that already built an
 * index do not force a second computation; passing only `(row, uploads)`
 * still works and computes evidence on demand.
 *
 * `nosorter` and `nothing` match zero rows among SKUs in the shipped
 * dataset, because every Nobero SKU has at least one sorter reading — but
 * ten of the sixty COMBOS genuinely have `sorterCount === 0`, so these are
 * not universally-empty filters, just empty for one of the two row kinds.
 * The UI shows a live count beside each, so a zero reads as a fact about the
 * account rather than as a broken filter.
 */
export const COVERAGE_FILTERS = [
  {id: 'all', label: 'All SKUs', match: () => true},
  {id: 'nomev', label: 'Missing merchant evidence', match: (r, u, e) => (e || dwsEvidence(r, u)).merchant === 0},
  {id: 'both', label: 'Both sources on file', match: (r, u, e) => (e || dwsEvidence(r, u)).both},
  {id: 'nothing', label: 'No DWS data at all', match: (r, u, e) => (e || dwsEvidence(r, u)).none},
  {id: 'nosorter', label: 'No sorter images', match: (r) => (r.sorterCount || 0) === 0},
  {id: 'lo30', label: 'Under 30% sorter coverage', match: (r, u, e) => (e || dwsEvidence(r, u)).sorterPct < 30},
  {id: 'lo60', label: 'Under 60% sorter coverage', match: (r, u, e) => (e || dwsEvidence(r, u)).sorterPct < 60},
  {id: 'hi', label: '60% sorter coverage and over', match: (r, u, e) => (e || dwsEvidence(r, u)).sorterPct >= 60},
]

const FILTER_BY_ID = COVERAGE_FILTERS.reduce((m, f) => Object.assign(m, {[f.id]: f}), {})

/**
 * Filtered AND sorted, lowest sorter coverage first, ties to the busiest
 * row — that is the row where the missing evidence is costing the most
 * money. Builds one evidence index for the (rows, uploads) pair so the
 * filter predicate and the sort comparator share the same computation
 * instead of each recomputing `dwsEvidence` per row.
 *
 * Selection is not this module's business: this returns rows, and a caller
 * that keeps a selected row id should resolve it against the full,
 * unfiltered row list so narrowing the filter never closes an open gallery.
 */
export function filterSkus(rows, {q = '', coverage = 'all'} = {}, uploads) {
  const term = String(q).trim().toLowerCase()
  const filter = FILTER_BY_ID[coverage] || FILTER_BY_ID.all
  const list = rows || []
  const index = buildEvidenceIndex(list, uploads)

  return list
    .filter((r) => {
      if (term && !rowKey(r).toLowerCase().includes(term) && !String(r.sub || '').toLowerCase().includes(term)) {
        return false
      }
      return filter.match(r, uploads, index.get(rowKey(r)))
    })
    .sort((a, b) => {
      const ea = index.get(rowKey(a))
      const eb = index.get(rowKey(b))
      return ea.sorterPct - eb.sorterPct || (b.ships || 0) - (a.ships || 0)
    })
}

/** A live count for every filter, over the same evidence index, built once. */
export function coverageCounts(rows, uploads) {
  const list = rows || []
  const index = buildEvidenceIndex(list, uploads)
  return COVERAGE_FILTERS.reduce((acc, f) => {
    acc[f.id] = list.filter((r) => f.match(r, uploads, index.get(rowKey(r)))).length
    return acc
  }, {})
}

export function dwsStats(rows, uploads) {
  const list = rows || []
  const index = buildEvidenceIndex(list, uploads)
  let ships = 0; let sorter = 0; let merchantRecords = 0
  let noMerchant = 0; let both = 0; let noSorter = 0; let lowCoverage = 0

  list.forEach((r) => {
    const e = index.get(rowKey(r))
    ships += r.ships || 0
    sorter += e.sorter
    merchantRecords += e.merchant
    if (e.merchant === 0) noMerchant++
    if (e.both) both++
    if (e.sorter === 0) noSorter++
    if (e.sorterPct < 30) lowCoverage++
  })

  return {
    skus: list.length,
    ships,
    sorter,
    merchantRecords,
    noMerchant,
    both,
    noSorter,
    lowCoverage,
    // Unrounded, same reason as dwsEvidence's percentages.
    coveragePct: ships ? (sorter / ships) * 100 : 0,
  }
}
